import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { doc, getDoc, setDoc, addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ArrowTopRightOnSquareIcon, XMarkIcon, EyeIcon } from '@heroicons/react/24/solid';
import { motion, AnimatePresence } from 'framer-motion';
import { db } from '../firebase';
import { useUser } from '../userContext';
import Loading from '../common/DefaultLoading';
import AlertError from '../dashboard/PermissionDenied';
import { getCampaignSearchFields } from '../utils/campaignSearchUtils';
import { updateCampaignStep } from '../common/Database';
import { gameTypes } from '../data/GameTypes';
import { cacheHelpers } from '../utils/campaignCache';

// Wizard steps reuse low-level building blocks (ColorPicker, FormInput) rather
// than the heavy editor sections - keeps the mobile flow tight. Imported
// eagerly (not lazy) because the chunk download mid-flow caused visible
// stalls on mobile networks when advancing steps.
import WizardGameType from './WizardGameType';
import WizardBrand from './WizardBrand';
import WizardContent from './WizardContent';
import { SparklesIcon } from '@heroicons/react/24/outline';

// Game types whose primary asset is generated content (quiz questions, list
// items) rather than visual assets. These get an extra "content" wizard step
// that captures a topic and auto-fills the campaign via existing AI endpoints.
const CONTENT_GAME_TYPES = new Set(['quizrush', 'completelist']);

// Per-game-type logo step config. Each game type that has a brand-bearing
// image asset gets its own copy + Firestore field. Game types without one
// (snake, quizrush, completelist) have no logo field in the brand step.
// Exported so WizardBrand can import it without circular deps if needed.
export const LOGO_CONFIG = {
    runner: { field: 'collectibleImage', nameKey: 'collectibleImageName', title: 'Add your logo', body: "This is what players collect as they run. Square images work best." },
    sidescroller: { field: 'collectibleImage', nameKey: 'collectibleImageName', title: 'Add your logo', body: "This is what players collect as they run. Square images work best." },
    flappy: { field: 'collectibleImage', nameKey: 'collectibleImageName', title: 'Add your logo', body: "This is what players collect as they fly. Square images work best." },
    verticalclimber: { field: 'collectibleImage', nameKey: 'collectibleImageName', title: 'Add your logo', body: "This is what players collect as they bounce. Square images work best." },
    minigolf: { field: 'flagImage', nameKey: 'flagImageName', title: 'Brand the flag', body: "Your logo flies on the hole flag, visible on every shot. Square images work best." },
    stacker: { field: 'blockImage', nameKey: 'blockImageName', title: 'Brand the block', body: "Each block players stack will use this image. Square images work best." },
};

const buildSteps = (gameTypeId) => {
    const logoConfig = gameTypeId ? (LOGO_CONFIG[gameTypeId] || null) : null;
    const steps = [
        { key: 'gameType', title: 'Pick a game', body: 'You can switch later.' },
        { key: 'brand', title: 'Make it yours', body: 'All fields are optional. Fill in what you have.', logoConfig },
    ];
    if (gameTypeId && CONTENT_GAME_TYPES.has(gameTypeId)) {
        const isQuiz = gameTypeId === 'quizrush';
        steps.push({
            key: 'content',
            title: isQuiz ? 'Quiz questions' : 'List items',
            body: isQuiz
                ? 'Tell us what the quiz is about and we\'ll write the questions for you. You can edit them later.'
                : 'Tell us what the list is about and we\'ll generate the items for you. You can edit them later.',
        });
    }
    return steps;
};

// Mirrors Editor.sanitizeOptions - keep them in sync.
function sanitizeCampaign(data) {
    const sanitized = JSON.parse(JSON.stringify(data, (key, value) => {
        if ((key === 'cta_text' || key === 'cta_link') && value === undefined) return '';
        return value === undefined ? null : value;
    }));
    if (sanitized.options) {
        sanitized.options.cta_text = sanitized.options.cta_text || '';
        sanitized.options.cta_link = sanitized.options.cta_link || '';
    }
    if (sanitized.gameType === 'quizrush' && sanitized.options?.leaderboard) {
        sanitized.options.leaderboard = { ...sanitized.options.leaderboard, allowMultipleAttempts: false };
    }
    return sanitized;
}

export default function Wizard() {
    const params = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { user, businessProfile, prependCampaign } = useUser();
    const isNew = params.id === 'new';
    // ?gameType=<id> pre-selects on the picker. Used by dashboard tiles that
    // already know which game the user wants - the picker still shows so they
    // can change their mind, but the right-panel preview lights up immediately
    // and Next is enabled.
    const presetGameTypeId = searchParams.get('gameType') || null;
    const [stepIndex, setStepIndex] = useState(0);
    // For pre-creation flow ("new"), we hold the user's game-type pick locally
    // until they tap Next; that's when the campaign is actually created.
    const [pendingGameTypeId, setPendingGameTypeId] = useState(presetGameTypeId);
    const [gameData, setGameData] = useState(null);
    // Brand step lifts its Generate / Skip controls into the wizard footer so
    // there's a single primary CTA bar across all steps. The ref carries the
    // latest action closures; the state object drives the buttons' enabled
    // and loading display.
    const brandControllerRef = useRef({ generate: () => {}, skip: () => {} });
    const [brandState, setBrandState] = useState({ canGenerate: false, isLoading: false, inReview: false, phase: 'form' });
    // Mirror pattern for the content step (quiz / complete-list AI generation).
    const contentControllerRef = useRef({ generate: () => {}, skip: () => {} });
    const [contentState, setContentState] = useState({ canGenerate: false, isLoading: false, inReview: false });
    const [isLoading, setIsLoading] = useState(!isNew);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const retryCountRef = useRef(0);

    // Compute step list at the top so handlers + render share one source.
    // Recomputes when game type changes (some types skip the logo step).
    const activeGameTypeId = gameData?.gameType || pendingGameTypeId;
    const steps = buildSteps(activeGameTypeId);

    // Match Editor: lock body scroll while mounted so iOS doesn't rubber-band
    // and the wizard is the only scroller.
    useEffect(() => {
        if (typeof window === 'undefined') return;
        const htmlEl = document.documentElement;
        const bodyEl = document.body;
        const prev = {
            htmlOverflow: htmlEl.style.overflow,
            bodyOverflow: bodyEl.style.overflow,
            htmlOverscroll: htmlEl.style.overscrollBehavior,
            bodyOverscroll: bodyEl.style.overscrollBehavior,
            htmlHeight: htmlEl.style.height,
            bodyHeight: bodyEl.style.height,
        };
        htmlEl.style.overflow = 'hidden';
        bodyEl.style.overflow = 'hidden';
        htmlEl.style.overscrollBehavior = 'none';
        bodyEl.style.overscrollBehavior = 'none';
        htmlEl.style.height = '100%';
        bodyEl.style.height = '100%';
        return () => {
            htmlEl.style.overflow = prev.htmlOverflow || '';
            bodyEl.style.overflow = prev.bodyOverflow || '';
            htmlEl.style.overscrollBehavior = prev.htmlOverscroll || '';
            bodyEl.style.overscrollBehavior = prev.bodyOverscroll || '';
            htmlEl.style.height = prev.htmlHeight || '';
            bodyEl.style.height = prev.bodyHeight || '';
        };
    }, []);

    // We deliberately don't track visualViewport.height here. Letting the
    // keyboard overlay the wizard rather than shrink it keeps the layout
    // stable - iOS auto-scrolls the inner scroller to keep the focused input
    // visible above the keyboard. Tracking would cause noticeable resize
    // jitter on focus/blur.

    const fetchCampaign = useCallback(async () => {
        if (!params.id || params.id === 'new' || !businessProfile?.id) return;
        setIsLoading(true);
        setError(null);
        try {
            const docRef = doc(db, 'campaign', params.id);
            const snap = await getDoc(docRef);
            if (!snap.exists()) { setError('Campaign not found.'); return; }
            const data = snap.data();
            if (data.companyId !== businessProfile.id) {
                setError("You don't have access to this game.");
                return;
            }
            retryCountRef.current = 0;
            setGameData(data);
        } catch (err) {
            console.error('Error fetching campaign:', err);
            if (retryCountRef.current < 1) {
                retryCountRef.current += 1;
                setTimeout(() => fetchCampaign(), 1000);
                return;
            }
            setError('An error occurred while fetching the campaign.');
        } finally {
            setIsLoading(false);
        }
    }, [params.id, businessProfile?.id]);

    useEffect(() => {
        if (isNew) return;
        if (!user || !params.id || !businessProfile?.id) return;
        fetchCampaign();
    }, [isNew, params.id, user, businessProfile?.id, fetchCampaign]);

    // Creates the campaign once the user picks a game type and taps Next.
    // After this resolves we replace the URL with the real id so refreshes
    // resume mid-wizard rather than restarting from the type picker.
    const createCampaignFromType = async (gameTypeId) => {
        const type = gameTypes.find(t => t.id === gameTypeId);
        if (!type || !businessProfile?.id) return null;
        const baseOptions = { ...(type.options || {}) };
        const sanitizedOptions = {
            ...baseOptions,
            cta_text: baseOptions.cta_text || '',
            cta_link: baseOptions.cta_link || '',
        };
        if (gameTypeId === 'quizrush' && sanitizedOptions.leaderboard) {
            sanitizedOptions.leaderboard = { ...sanitizedOptions.leaderboard, allowMultipleAttempts: false };
        }
        // Default internal name = "<Game Type> <D MMM>". Saves the user from
        // seeing the "Add internal name" prompt on the campaign page right
        // after finishing the wizard.
        const today = new Date();
        const dateLabel = today.toLocaleString('en-GB', { day: 'numeric', month: 'short' });
        const defaultInternalName = `${type.name} ${dateLabel}`;
        const campaignData = {
            gameType: type.id,
            gameTypeDisplayValue: type.name,
            name: baseOptions.title || type.name,
            internalName: defaultInternalName,
            status: 'draft',
            arcade: false,
            private: true,
            options: sanitizedOptions,
            createdAt: serverTimestamp(),
            createdBy: user?.uid,
            companyId: businessProfile.id,
        };
        const { _searchInternalName, _searchTitle } = getCampaignSearchFields(campaignData);
        const docRef = await addDoc(collection(db, 'campaign'), {
            ...campaignData,
            _searchInternalName,
            _searchTitle,
        });
        cacheHelpers.invalidateUserCampaigns(businessProfile.id);
        if (typeof prependCampaign === 'function') {
            prependCampaign({ id: docRef.id, ...campaignData, createdAt: new Date() });
        }
        return docRef.id;
    };

    const handleOptionsUpdate = (updatedOptions) => {
        setGameData(prev => prev ? ({ ...prev, options: { ...prev.options, ...updatedOptions } }) : prev);
    };

    // Fire-and-forget save. We don't block the Next transition on the
    // network round-trip: the user perceives Next as instant, and the write
    // happens in the background. If it fails we show a toast - the data is
    // still in local state, the user can hit Next again or manually retry.
    const saveCurrentStep = (snapshot) => {
        const data = snapshot || gameData;
        if (!data || !params.id || params.id === 'new') return;
        const docRef = doc(db, 'campaign', params.id);
        const sanitized = sanitizeCampaign(data);
        const { _searchInternalName, _searchTitle } = getCampaignSearchFields(sanitized);
        // Don't await - Promise.resolve().then() ensures errors don't bubble.
        updateCampaignStep(params.id, 3).catch(() => { /* non-fatal */ });
        setDoc(docRef, {
            ...sanitized,
            _searchInternalName,
            _searchTitle,
            editedInEditor: true,
            lastEditedAt: new Date().toISOString(),
        }, { merge: true }).catch((err) => {
            console.error('Wizard background save failed:', err);
            toast.error("Couldn't save. Check your connection.", { position: 'top-center' });
        });
    };

    // optionsSnapshot: if the caller has already applied options locally
    // (via handleOptionsUpdate) but React hasn't flushed state yet, pass the
    // merged options here so saveCurrentStep persists the right data.
    // Guard against being passed a synthetic React event when used as a raw
    // onClick handler - only accept plain objects.
    const handleNext = async (optionsSnapshot) => {
        const validSnapshot =
            optionsSnapshot && typeof optionsSnapshot === 'object' && !optionsSnapshot.nativeEvent
                ? optionsSnapshot
                : undefined;
        // Step 1 (game type) for a brand-new campaign creates the campaign,
        // updates the URL to the real id, and advances. Subsequent steps just
        // save and advance.
        const currentStep = steps[stepIndex];
        if (currentStep?.key === 'gameType' && isNew) {
            if (!pendingGameTypeId) return;
            setIsSaving(true);
            try {
                const newId = await createCampaignFromType(pendingGameTypeId);
                if (!newId) throw new Error('Failed to create campaign');
                // The component instance stays mounted across param changes
                // in react-router v6, so advancing stepIndex must happen
                // explicitly here - useState's initialiser only fires once.
                setStepIndex(1);
                navigate(`/wizard/${newId}`, { replace: true });
            } catch (err) {
                console.error('Wizard create failed:', err);
                toast.error("Couldn't create your game. Try again.", { position: 'top-center' });
            } finally {
                setIsSaving(false);
            }
            return;
        }

        // Save fires in the background - don't make the user wait. The
        // step transition feels instant. When a snapshot is provided (e.g.
        // from WizardBrand after applying theme before setState flushes),
        // use it so the save includes the just-applied changes.
        const snapshot = validSnapshot
            ? { ...gameData, options: { ...(gameData?.options || {}), ...validSnapshot } }
            : undefined;
        saveCurrentStep(snapshot);
        if (stepIndex >= steps.length - 1) {
            // Wizard complete — drop the user straight into a live preview of
            // the game they just configured. The play page's "Exit preview"
            // affordance lands them on the campaign detail page.
            //
            // We REPLACE the wizard entry in history with the full editor
            // first, then PUSH the preview on top. Browser back from the
            // preview now lands the user in the editor (where they can keep
            // tweaking) instead of back into the wizard they just finished.
            toast.success('Game ready. Have a play.', { position: 'top-center', autoClose: 4000 });
            navigate(`/editor/${params.id}`, { replace: true });
            navigate(`/p/${params.id}?preview=true&fromWizard=1`);
            return;
        }
        setStepIndex(i => i + 1);
        const scroller = document.querySelector('[data-wizard-scroll]');
        if (scroller) scroller.scrollTop = 0;
    };

    const handleBack = () => {
        if (stepIndex === 0) {
            navigate(`/studio/dashboard`);
            return;
        }
        setStepIndex(i => i - 1);
    };

    const handleSkipToEditor = () => {
        // Pre-creation: no editor to skip to. Use the dashboard so the user
        // can still bail out cleanly.
        if (isNew) {
            navigate('/studio/dashboard');
            return;
        }
        saveCurrentStep();
        navigate(`/editor/${params.id}`);
    };

    if (isLoading) return <Loading />;
    if (error) return <AlertError text={error} onRetry={() => { retryCountRef.current = 0; fetchCampaign(); }} />;
    // Only require gameData once we're past the pre-creation step (the
    // game-type picker doesn't need a campaign yet).
    if (!isNew && !gameData) return <Loading />;

    const step = steps[stepIndex];
    const isLast = stepIndex === steps.length - 1;

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.35, ease: 'easeOut' }}
            className="admin-theme gch-layout-bg flex flex-col overflow-hidden fixed inset-0"
            style={{ height: '100dvh' }}
        >
            <ToastContainer />

            {/* Header */}
            <div className="shrink-0 px-3 sm:px-5 h-14 flex items-center justify-between border-b border-[var(--admin-border)] gch-bg-card">
                {/* Left: step counter on desktop, nothing on mobile (back is in footer) */}
                <div className="flex items-center gap-3">
                    <span className="hidden lg:block text-[10px] font-bold uppercase tracking-wider text-[var(--admin-accent)]">
                        Step {stepIndex + 1} of {steps.length}
                    </span>
                    <div className="flex items-center gap-1.5">
                        {steps.map((_, i) => (
                            <span
                                key={i}
                                className={`h-1.5 w-6 rounded-full transition-colors ${
                                    i <= stepIndex ? 'bg-[var(--admin-accent)]' : 'bg-white/15'
                                }`}
                            />
                        ))}
                    </div>
                </div>
                <button
                    type="button"
                    onClick={handleSkipToEditor}
                    className="btn-admin-outline inline-flex items-center gap-1.5 rounded-lg px-3 h-8 text-xs font-semibold"
                    title="Skip the wizard and use the full editor"
                >
                    Open editor
                </button>
            </div>

            {/* Step intro — hidden during the brand step's loading takeover
                / review screens; the inline panels carry their own headlines. */}
            {!(step.key === 'brand' && (brandState.isLoading || brandState.inReview)) && (
                <div className="shrink-0 px-4 pt-4 pb-3 border-b border-[var(--admin-border)]">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-[var(--admin-accent)] mb-1 lg:hidden">
                        Step {stepIndex + 1} of {steps.length}
                    </p>
                    <h1 className="text-xl gch-heading tracking-tight gch-text-primary">{step.title}</h1>
                    {step.body && <p className="text-sm gch-text-secondary mt-1">{step.body}</p>}
                </div>
            )}

            {/* Two-pane at lg+: left = scrollable controls, right = persistent phone preview */}
            <div className="flex-1 min-h-0 flex overflow-hidden">

                {/* Left: scrollable controls */}
                <div data-wizard-scroll className="flex-1 min-h-0 min-w-0 overflow-y-auto overscroll-contain studio-scrollbar lg:flex lg:flex-col">
                    <AnimatePresence mode="wait" initial={false}>
                    <motion.div
                        key={step.key}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.22, ease: 'easeOut' }}
                        className={`p-4 w-full ${
                        step.key === 'gameType'
                            ? 'max-w-md mx-auto lg:max-w-5xl lg:px-10 lg:py-8 lg:my-auto'
                            : step.key === 'review'
                                ? 'max-w-md mx-auto lg:mx-auto lg:max-w-2xl lg:py-8 lg:my-auto'
                                : 'max-w-md mx-auto lg:mx-auto lg:max-w-lg lg:py-8'
                    }`}>
                        {step.key === 'gameType' && (
                            <WizardGameType
                                selectedTypeId={pendingGameTypeId || gameData?.gameType || null}
                                onSelect={setPendingGameTypeId}
                            />
                        )}
                        {/* Steps after game-type need the campaign loaded.
                            On the brief gap between create + fetch we show
                            the loader instead of crashing on null options. */}
                        {step.key !== 'gameType' && !gameData && <Loading />}
                        {step.key === 'brand' && gameData && (
                            <div className="space-y-5">
                                <WizardBrand
                                    gameData={gameData}
                                    handleOptionsUpdate={handleOptionsUpdate}
                                    onComplete={handleNext}
                                    logoConfig={step.logoConfig || null}
                                    controllerRef={brandControllerRef}
                                    onStateChange={setBrandState}
                                />
                            </div>
                        )}
                        {step.key === 'content' && gameData && (
                            <WizardContent
                                gameType={gameData.gameType}
                                gameData={gameData}
                                handleOptionsUpdate={handleOptionsUpdate}
                                onComplete={handleNext}
                                controllerRef={contentControllerRef}
                                onStateChange={setContentState}
                            />
                        )}
                        {/* Review step removed: after the brand step the wizard
                            navigates straight into the live preview at /p/:id?preview=true
                            so the user sees their actual game. The play page's
                            "Exit preview" affordance lands them on the campaign page. */}
                    </motion.div>
                    </AnimatePresence>
                </div>
            </div>

            {/* Footer nav. */}
            <div className="shrink-0 px-4 sm:px-6 py-3 border-t border-[var(--admin-border)] gch-bg-card flex items-center gap-2 sm:gap-3">
                <button
                    type="button"
                    onClick={handleBack}
                    className="btn-admin-outline btn-admin-md shrink-0"
                    disabled={isSaving}
                >
                    {stepIndex === 0 ? 'Cancel' : 'Back'}
                </button>
                {/* Brand step has Skip + Generate; everything else has Next. */}
                {step.key === 'gameType' && isSaving ? (
                    <button
                        type="button"
                        disabled
                        className="btn-admin-primary btn-admin-md inline-flex items-center justify-center gap-2 disabled:opacity-90 ml-auto"
                    >
                        <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 000 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z" />
                        </svg>
                        Creating your game…
                    </button>
                ) : step.key === 'brand' ? (
                    brandState.inReview ? (
                        <>
                            <button
                                type="button"
                                onClick={() => brandControllerRef.current?.regenerate?.()}
                                disabled={isSaving}
                                className="btn-admin-outline btn-admin-md ml-auto shrink-0 disabled:opacity-50"
                            >
                                Regenerate
                            </button>
                            <button
                                type="button"
                                onClick={() => brandControllerRef.current?.confirm?.()}
                                disabled={isSaving}
                                className="btn-admin-primary btn-admin-md inline-flex items-center justify-center gap-2 disabled:opacity-50 min-w-0 whitespace-nowrap"
                            >
                                Preview →
                            </button>
                        </>
                    ) : brandState.isLoading ? (
                        // During the loading takeover the inline panel handles
                        // the spinner; footer shows nothing actionable.
                        <div className="ml-auto" />
                    ) : (
                        <>
                            <button
                                type="button"
                                onClick={() => brandControllerRef.current?.skip?.()}
                                disabled={isSaving}
                                className="btn-admin-outline btn-admin-md ml-auto shrink-0 disabled:opacity-50"
                            >
                                Skip
                            </button>
                            <button
                                type="button"
                                onClick={() => brandControllerRef.current?.generate?.()}
                                disabled={!brandState.canGenerate || isSaving}
                                className="btn-admin-primary btn-admin-md inline-flex items-center justify-center gap-2 disabled:opacity-50 min-w-0 whitespace-nowrap"
                            >
                                <SparklesIcon className="w-4 h-4" />
                                Generate
                            </button>
                        </>
                    )
                ) : step.key === 'content' ? (
                    contentState.inReview ? (
                        <>
                            <button
                                type="button"
                                onClick={() => contentControllerRef.current?.regenerate?.()}
                                disabled={isSaving}
                                className="btn-admin-outline btn-admin-md ml-auto shrink-0 disabled:opacity-50"
                            >
                                Regenerate
                            </button>
                            <button
                                type="button"
                                onClick={() => handleNext()}
                                disabled={isSaving}
                                className="btn-admin-primary btn-admin-md inline-flex items-center justify-center gap-2 disabled:opacity-50 min-w-0 whitespace-nowrap"
                            >
                                {isLast ? 'Go to campaign →' : 'Next →'}
                            </button>
                        </>
                    ) : contentState.isLoading ? (
                        <div className="ml-auto" />
                    ) : (
                        <>
                            <button
                                type="button"
                                onClick={() => handleNext()}
                                disabled={isSaving}
                                className="btn-admin-outline btn-admin-md ml-auto shrink-0 disabled:opacity-50"
                            >
                                Skip
                            </button>
                            <button
                                type="button"
                                onClick={() => contentControllerRef.current?.generate?.()}
                                disabled={!contentState.canGenerate || isSaving}
                                className="btn-admin-primary btn-admin-md inline-flex items-center justify-center gap-2 disabled:opacity-50 min-w-0 whitespace-nowrap"
                            >
                                <SparklesIcon className="w-4 h-4" />
                                Generate
                            </button>
                        </>
                    )
                ) : (
                    <button
                        type="button"
                        onClick={() => handleNext()}
                        disabled={isSaving || (step.key === 'gameType' && !pendingGameTypeId && !gameData?.gameType)}
                        className="btn-admin-primary btn-admin-md inline-flex items-center justify-center gap-2 disabled:opacity-50 ml-auto"
                    >
                        {isSaving ? 'Saving…' : (isLast ? 'Go to campaign →' : 'Next →')}
                    </button>
                )}
            </div>
        </motion.div>
    );
}
